import { useReducer } from 'react'
import { IDHash } from '@api'
import { omit } from 'lodash'

export interface UseSelectionHook {
  items: IDHash<boolean>
  selectedSpacesList: string[]
  count: number
  toggle: (id: string) => void
  add: (ids: string[]) => void
  remove: (ids: string[]) => void
  clear: () => void
}

enum Actions {
  TOGGLE,
  ADD,
  CLEAR,
  REMOVE,
}

interface ToggleAction {
  type: Actions.TOGGLE
  id: string
}

interface AddAction {
  type: Actions.ADD
  ids: string[]
}

interface RemoveAction {
  type: Actions.REMOVE
  ids: string[]
}

interface ClearAction {
  type: Actions.CLEAR
}

type RootAction = ToggleAction | AddAction | ClearAction | RemoveAction

function reducer(state: IDHash<boolean>, action: RootAction) {
  switch (action.type) {
    case Actions.TOGGLE:
      return state[action.id] ? {} : { [action.id]: true }

    case Actions.ADD:
      return {
        ...state,
        ...action.ids.reduce((result, id) => {
          return {
            ...result,
            [id]: true,
          }
        }, {}),
      }

    case Actions.REMOVE:
      return omit(state, action.ids)

    case Actions.CLEAR:
      return {}

    default:
      return state
  }
}

export default function useSelection(): UseSelectionHook {
  const [items, dispatch] = useReducer(reducer, {})

  return {
    items,
    selectedSpacesList: Object.keys(items),
    get count() {
      return Object.keys(items).length
    },
    toggle: (id: string) => dispatch({ type: Actions.TOGGLE, id }),
    add: (ids: string[]) => dispatch({ type: Actions.ADD, ids }),
    remove: (ids: string[]) => dispatch({ type: Actions.REMOVE, ids }),
    clear: () => dispatch({ type: Actions.CLEAR }),
  }
}
